
import React, { createContext, useContext, useState, ReactNode } from 'react';
import { SwipeableNotification, SwipeableNotificationTitle, SwipeableNotificationDescription } from '@/components/ui/swipeable-notification';

type NotificationVariant = 'default' | 'destructive' | 'success';

interface NotificationOptions {
  title?: string;
  description?: string;
  duration?: number;
  variant?: NotificationVariant;
}

interface NotificationContextType {
  showNotification: (options: NotificationOptions) => void;
  hideNotification: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notificationData, setNotificationData] = useState<NotificationOptions>({
    title: '',
    description: '',
    duration: 3000,
    variant: 'default'
  });

  const showNotification = (options: NotificationOptions) => {
    setNotificationData({
      title: options.title || '',
      description: options.description || '',
      duration: options.duration || 3000,
      variant: options.variant || 'default'
    });
    setIsOpen(true);
  };

  const hideNotification = () => {
    setIsOpen(false);
  };

  // Position the notification at the top of the screen
  return (
    <NotificationContext.Provider value={{ showNotification, hideNotification }}>
      {children}
      <div className="fixed top-4 inset-x-0 z-[100] flex justify-center pointer-events-none">
        <div className="w-full max-w-sm mx-4">
          {isOpen && (
            <SwipeableNotification 
              open={isOpen} 
              onOpenChange={setIsOpen}
              duration={notificationData.duration}
              variant={notificationData.variant}
              className="pointer-events-auto"
            >
              {notificationData.title && (
                <SwipeableNotificationTitle>
                  {notificationData.title}
                </SwipeableNotificationTitle>
              )}
              {notificationData.description && (
                <SwipeableNotificationDescription>
                  {notificationData.description}
                </SwipeableNotificationDescription>
              )}
            </SwipeableNotification>
          )}
        </div>
      </div>
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
